import React from 'react';
import {
	Box,
	Chip,
	Divider,
	Stack,
	Tooltip,
	Typography,
	useTheme,
} from '@mui/material';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import SectionHeader from './SectionHeader';
import { formatCurrency } from '../../../../Shared/stripeFee';
import { surfaceCard } from '../../../../Shared/surfaceCard';

const TYPE_LABELS = {
	deposit: 'Deposit',
	final: 'Final payment',
	refund: 'Refund',
	void: 'Void',
};

const METHOD_LABELS = {
	stripe: 'Stripe',
	check: 'Check',
	venmo: 'Venmo',
	other: 'Other',
};

const STATUS_COLORS = {
	succeeded: 'success',
	voided: 'default',
	pending: 'warning',
	failed: 'error',
	canceled: 'default',
};

const TYPE_COLORS = {
	deposit: 'success.main',
	final: 'success.main',
	refund: 'warning.main',
	void: 'error.main',
};

const formatStripeReference = (reference) => {
	if (!reference) {
		return null;
	}
	if (reference.length <= 16) {
		return reference;
	}
	return `${reference.slice(0, 10)}...${reference.slice(-4)}`;
};

const JobPaymentsTimeline = ({ paymentTimeline = [] }) => {
	const theme = useTheme();

	if (!paymentTimeline.length) {
		return null;
	}

	return (
		<Box
			sx={{
				...surfaceCard(theme),
				display: 'flex',
				flexDirection: 'column',
				maxHeight: 320,
				minHeight: 0,
			}}
		>
			<SectionHeader icon={PaymentsOutlinedIcon} title="Payments" />
			<Divider sx={{ opacity: 0.6 }} />
			<Box
				sx={{
					flex: 1,
					minHeight: 0,
					overflowY: 'auto',
					px: { xs: 2, sm: 2.5 },
					py: 1.5,
				}}
			>
				<Stack spacing={0}>
					{paymentTimeline.map((entry, index) => {
						const stripeRef = formatStripeReference(entry.stripe_reference_id);
						return (
							<Box
								key={entry.id}
								sx={{
									py: 1.25,
									borderBottom:
										index === paymentTimeline.length - 1 ? 'none' : '1px solid',
									borderColor: 'divider',
								}}
							>
								<Stack
									direction="row"
									justifyContent="space-between"
									alignItems="flex-start"
									spacing={1}
								>
									<Box sx={{ minWidth: 0 }}>
										<Typography variant="body2" fontWeight={600}>
											<Box
												component="span"
												sx={{
													color: TYPE_COLORS[entry.type] || 'text.primary',
												}}
											>
												{TYPE_LABELS[entry.type] || entry.type}
											</Box>
											<Box
												component="span"
												sx={{
													fontVariantNumeric: 'tabular-nums',
													ml: 1,
												}}
											>
												${formatCurrency(entry.amount)}
											</Box>
											{entry.method ? (
												<Box
													component="span"
													sx={{
														color: 'text.secondary',
														fontWeight: 500,
														ml: 1,
													}}
												>
													{METHOD_LABELS[entry.method] || entry.method}
												</Box>
											) : null}
										</Typography>
										{entry.notes ? (
											<Typography
												variant="body2"
												color="text.secondary"
												sx={{ mt: 0.25 }}
											>
												{entry.notes}
											</Typography>
										) : null}
										<Typography
											variant="caption"
											color="text.secondary"
											display="block"
											sx={{ mt: 0.25 }}
										>
											{new Date(entry.occurred_at).toLocaleString(undefined, {
												dateStyle: 'medium',
												timeStyle: 'short',
											})}
											{stripeRef ? (
												<>
													{' · '}
													<Tooltip title={entry.stripe_reference_id}>
														<Box component="span">{stripeRef}</Box>
													</Tooltip>
												</>
											) : null}
										</Typography>
									</Box>
									<Chip
										label={entry.status}
										size="small"
										color={STATUS_COLORS[entry.status] || 'default'}
										sx={{ textTransform: 'capitalize', flexShrink: 0 }}
									/>
								</Stack>
							</Box>
						);
					})}
				</Stack>
			</Box>
		</Box>
	);
};

export default JobPaymentsTimeline;
